import axios from 'axios';
import axiosRetry from 'axios-retry';
import _get from 'lodash/get';
import _isObject from 'lodash/isObject';
import { getReleaseStage } from 'src/services/bugsnag';
import errorService from 'src/services/error';

const instance = axios.create({
  baseURL: process.env.API_BASE_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

axiosRetry(instance, {
  retries: 3,
  shouldResetTimeout: true,
  retryDelay: (retryCount) => {
    // estimated GAE boot time is 15s
    const exponent = [1, 3, 4][retryCount - 1];
    return 2 ** exponent * 1000;
  },
});

/**
 * Performs a request using the given parameters.
 *
 * @param {Object} payload
 * @param {String} payload.method
 * @param {String} payload.url
 * @param {Object} payload.data
 * @param {Object} payload.config
 * @returns {Object}
 * @throws {Error}
 */
async function request({ method, url, config, data }) {
  const requestArgs = [url];

  if (data) {
    requestArgs.push(data);
  }

  requestArgs.push({
    ...config,
    validateStatus(status) {
      return inspectStatus({ config, status, url, method, data });
    },
  });

  try {
    const response = await instance[method](...requestArgs);
    return response;
  } catch (error) {
    const wasCancelled = axios.isCancel(error);
    const status = _get(error, 'response.status');
    const isUnauthenticatedAppBoot = status === 401 && url === '/auth/profile' && method === 'get';
    if (!wasCancelled && !isUnauthenticatedAppBoot) {
      const errorBody = _get(error, 'response.data');
      const isVehicleStyleRequest404 = status === 404 && url.includes('/api/vehicle-style') && method === 'get';
      if (!isVehicleStyleRequest404) {
        reportApiError({ body: errorBody, error, method, requestUrl: url, status });
      }
      throw error;
    }
    return {};
  }
}

/**
 * @param {string} url
 * @param {import('axios').AxiosRequestConfig} config
 */
async function get(url, config = {}) {
  return request({ method: 'get', url, config });
}

async function getAll(url, config = {}, onResponseCallback = () => {}, onBeforeRequest = () => {}) {
  let results = [];
  let dataProperty;
  // eslint-disable-next-line no-async-promise-executor
  const promise = new Promise(async (resolve, reject) => {
    try {
      const lastResponse = await getPage(url, config);
      resolve({ ...lastResponse, data: { [dataProperty]: results } });
    } catch (error) {
      reject(error);
    }
  });

  return promise;

  async function getPage(theUrl, theConfig) {
    const res = await get(theUrl, theConfig);
    if (_get(res, 'data', false) && !dataProperty) {
      dataProperty = Object.keys(res.data).filter(
        (key) => ['cursor', 'errors', 'ok', 'pagination', 'warnings'].indexOf(key) === -1
      );
    }
    results = [...results, ..._get(res, ['data', dataProperty], [])];

    const beforeNextRequest = onBeforeRequest();
    const { cancel } = _isObject(beforeNextRequest) ? beforeNextRequest : {};

    onResponseCallback({ ...res, cancelled: cancel, data: { [dataProperty]: results } });

    if (_get(res, 'data.cursor', false) && !cancel) {
      const params = { ...theConfig.params, ...{ cursor: _get(res, 'data.cursor') } };
      const newConfig = { ...theConfig, ...{ params } };
      return getPage(theUrl, newConfig);
    }

    return res;
  }
}

async function post(url, data, config = {}) {
  return request({ method: 'post', url, data, config });
}

async function put(url, data, config = {}) {
  return request({ method: 'put', url, data, config });
}

async function del(url, config = {}) {
  return request({ method: 'delete', url, config });
}

async function executeAll(requests) {
  return Promise.all(requests);
}

function setAccountIdHeader(accountId) {
  if (accountId) {
    instance.defaults.headers.common['zubie-account-id'] = accountId;
  }
}

function getInstance() {
  return instance;
}

/**
 * Reports the given error and meta data.
 *
 * @param {Object} payload
 * @param {Object} payload.body
 * @param {Error} payload.error
 * @param {String} payload.method
 * @param {Object} payload.response
 * @param {String} payload.requestUrl
 */
function reportApiError({ body, error, method, response, requestUrl, status }) {
  if (getReleaseStage() === 'support' && status === 401) {
    // don't log 401s from the support instance since they occur after the 30 minute auth expires, but the app keeps polling APIs
    return;
  }

  const metaData = {
    'Request Method': method,
    'Request Body': body,
    'Response Status': status,
  };

  if (response) {
    metaData['Request URL'] = response.request.responseURL;
    metaData['Response Text'] = response.request.responseText;
  } else {
    metaData['Request URL'] = requestUrl;
  }

  errorService.log(error, {
    metaData,
  });
}

/**
 * Reports any non-200 status and forwards the validation result using the provided config
 * (or uses the default validation logic).
 *
 * @param {Object} payload
 * @param {Object} payload.config
 * @param {Number} payload.status
 * @param {String} payload.url
 * @returns {Boolean}
 */
function inspectStatus({ config, status, url, method, data }) {
  // record errors for requests that have their own validateStatus function, which will cause them to not throw/be caught in the request function above
  const isPost400 = status === 400 && method === 'post';
  if (isPost400) {
    reportApiError({
      error: new Error(`Request failed with status code ${status}`),
      body: data,
      method,
      requestUrl: url,
      status,
    });
  }

  if (!config.validateStatus) {
    return (status >= 200 && status < 300) || status === 304; // 200 <= status < 300 is default
  }

  return config.validateStatus(status);
}

/**
 * @param {string} mediaKey
 * @param {{ filename?: string; url?: boolean }}
 */
function getMedia(mediaKey, { filename = '', url = false } = {}) {
  const mediaUrl = getMediaUrl(mediaKey, { filename, url });
  return get(mediaUrl);
}

/**
 * @param {string} mediaKey
 * @param {{ filename?: string; url?: boolean }}
 */
function getMediaUrl(mediaKey, { filename = '', url = false } = {}) {
  return `${process.env.API_BASE_URL}/api/v1/public/media/${mediaKey}/${url ? 'url' : 'fetch'}${filename ? `/${filename}` : ''}?timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
}

export default () => ({
  delete: del,
  get,
  getAll,
  getMedia,
  getMediaUrl,
  post,
  put,
  executeAll,
  setAccountIdHeader,
});

export { getInstance };
